// In hooks/articleHooks.js
import { bulkUploadArticles, bulkUpdateZoneEtage, getArticlesZoneEtage, listArticleDBFFiles, previewArticlesFromDBF, importArticlesFromDBF } from '../models/articleAPI';
import { useState, useEffect,useCallback } from 'react';
import { getAllArticles,getArticleById, updateArticle,createArticle,deleteArticle } from '../models/articleAPI';

export const useFetchAllArticles = (params = {}) => {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const { articles: fetchedArticles, total: fetchedTotal } = await getAllArticles(params);
      setArticles(fetchedArticles);
      setTotal(fetchedTotal);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [params]);
  
  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);
  
  return {
    articles,
    total,
    loading,
    error,
    refetch: fetchArticles
  };
};



export const useFetchArticleById = (id) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Define the fetching logic as a callable function
  const fetchArticle = useCallback(() => {
    if (id) {
      setLoading(true);
      setError('');  // Reset the error state on new fetch
      getArticleById(id)
        .then(response => {
          setArticle(response.data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [id]);

  // Call fetchArticle on initial mount and when `id` changes
  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  // Return everything including the fetchArticle function renamed as refetch for external usage
  return { article, loading, error, refetch: fetchArticle };
};


export const useUpdateArticle = () => {
  const [error, setError] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  const handleUpdate = async (id, articleData) => {
    try {
      await updateArticle(id, articleData);
      setIsUpdated(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setIsUpdated(false);
      throw new Error(err.response?.data?.message || err.message);
    }
  };

  return { handleUpdate, error, isUpdated };
};


export const useCreateArticle = () => {
  const [error, setError] = useState('');
  const [isCreated, setIsCreated] = useState(false);

  const handleCreate = async (articleData) => {
    try {
      await createArticle(articleData);
      setIsCreated(true);
    } catch (err) {
      setError(err.message);
      setIsCreated(false);
    }
  };

  return { handleCreate, error, isCreated };
};


export const useDeleteArticle = () => {
  const [error, setError] = useState('');
  const [isDeleted, setIsDeleted] = useState(false);

  const handleDelete = async (id) => {
    try {
      await deleteArticle(id);
      setIsDeleted(true);
    } catch (err) {
      setError(err.message);
      setIsDeleted(false);
    }
  };

  return { handleDelete, error, isDeleted };
};



export const useBulkUploadArticles = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleBulkUpload = async (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await bulkUploadArticles(formData);
      setUploadResult(response.data);
    } catch (err) {
      setError(err.message || 'An error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  return { handleBulkUpload, loading, error, uploadResult };
};

export const useListArticleDBFFiles = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await listArticleDBFFiles();
      setFiles(res.data || []);
      return res.data;
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { files, fetchFiles, loading, error };
};

export const usePreviewArticlesFromDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const previewArticles = async (fileName) => {
    try {
      setLoading(true);
      setError('');
      const res = await previewArticlesFromDBF(fileName);
      return res.data;
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  return { previewArticles, loading, error };
};

export const useImportArticlesFromDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const importArticles = async (articles) => {
    try {
      setLoading(true);
      setError('');
      const res = await importArticlesFromDBF(articles);
      return res.data;
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  return { importArticles, loading, error };
};

export const useBulkUpdateZoneEtage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updateResult, setUpdateResult] = useState(null);

  const handleBulkUpdateZoneEtage = async (articles, codeEnt = '2') => {
    setLoading(true);
    setError('');
    setUpdateResult(null);

    try {
      const response = await bulkUpdateZoneEtage({ articles, codeEnt });
      setUpdateResult(response.data);
    } catch (err) {
      setError(err.message || 'An error occurred during zone/etage update');
    } finally {
      setLoading(false);
    }
  };

  return { handleBulkUpdateZoneEtage, loading, error, updateResult };
};

export const useFetchArticlesZoneEtage = (codeEnt = '2') => {
  const [allArticles, setAllArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Initial fetch of first 100 articles
  const fetchInitialArticles = useCallback(async () => {
    setInitialLoading(true);
    setError('');
    
    try {
      const { articles: initialArticles, total: totalCount } = await getArticlesZoneEtage({
        page: 1,
        pageSize: 100,
        sortField: 'code_art',
        sortOrder: 'asc',
        codeEnt: codeEnt
      });
      
      setAllArticles(initialArticles);
      setFilteredArticles(initialArticles);
      setTotal(totalCount);
      
      // Start background fetch if there are more articles
      if (totalCount > 100) {
        fetchRemainingArticles(initialArticles, totalCount);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setInitialLoading(false);
    }
  }, [codeEnt]);

  // Background fetch of remaining articles
  const fetchRemainingArticles = useCallback(async (existingArticles, totalCount) => {
    setBackgroundLoading(true);
    
    try {
      const remainingCount = totalCount - 100;
      const pageSize = 500; // Fetch in larger chunks for efficiency
      const totalPages = Math.ceil(remainingCount / pageSize);
      let allRemainingArticles = [...existingArticles];
      
      for (let page = 1; page <= totalPages; page++) {
        const { articles: pageArticles } = await getArticlesZoneEtage({
          page: page + 1, // Start from page 2 since we already have page 1
          pageSize: pageSize,
          sortField: 'code_art',
          sortOrder: 'asc',
          codeEnt: codeEnt
        });
        
        allRemainingArticles = [...allRemainingArticles, ...pageArticles];
        
        // Update the state progressively
        setAllArticles([...allRemainingArticles]);
        // Only update filtered articles if no filters are currently applied
        setFilteredArticles(prevFiltered => {
          // If the current filtered articles equal the previous all articles, update them
          // This means no filters are currently active
          if (prevFiltered.length === allRemainingArticles.length - pageArticles.length) {
            return [...allRemainingArticles];
          }
          return prevFiltered;
        });
      }
    } catch (err) {
      console.error('Background fetch error:', err);
      // Don't set error for background fetch failures
    } finally {
      setBackgroundLoading(false);
    }
  }, [codeEnt]);

  // Filter function that works on the full dataset
  const applyFilters = useCallback((filters) => {
    const { searchTerm, zoneFilter, etageFilter, stockFilter } = filters;
    
    if (!searchTerm && !zoneFilter && !etageFilter && !stockFilter) {
      setFilteredArticles(allArticles);
      return;
    }

    const filtered = allArticles.filter(article => {
      let matches = true;

      // Search term filter (article name or code)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        matches = matches && (
          (article.code_art && article.code_art.toLowerCase().includes(searchLower)) ||
          (article.ARTICLE && article.ARTICLE.toLowerCase().includes(searchLower))
        );
      }

      // Zone filter
      if (zoneFilter) {
        const zoneLower = zoneFilter.toLowerCase();
        matches = matches && (
          article.Zone && article.Zone.toLowerCase().includes(zoneLower)
        );
      }

      // Etage filter
      if (etageFilter) {
        const etageValue = parseInt(etageFilter);
        if (!isNaN(etageValue)) {
          matches = matches && article.Etage === etageValue;
        }
      }

      // Stock filter (supports operators like >10, <5, =0, >=20, <=5)
      if (stockFilter) {
        const stockValue = article.qte_stk || 0;
        const filterTrimmed = stockFilter.trim();
        
        // Check for operators
        if (filterTrimmed.startsWith('>=')) {
          const threshold = parseInt(filterTrimmed.slice(2));
          if (!isNaN(threshold)) {
            matches = matches && stockValue >= threshold;
          }
        } else if (filterTrimmed.startsWith('<=')) {
          const threshold = parseInt(filterTrimmed.slice(2));
          if (!isNaN(threshold)) {
            matches = matches && stockValue <= threshold;
          }
        } else if (filterTrimmed.startsWith('>')) {
          const threshold = parseInt(filterTrimmed.slice(1));
          if (!isNaN(threshold)) {
            matches = matches && stockValue > threshold;
          }
        } else if (filterTrimmed.startsWith('<')) {
          const threshold = parseInt(filterTrimmed.slice(1));
          if (!isNaN(threshold)) {
            matches = matches && stockValue < threshold;
          }
        } else if (filterTrimmed.startsWith('=')) {
          const threshold = parseInt(filterTrimmed.slice(1));
          if (!isNaN(threshold)) {
            matches = matches && stockValue === threshold;
          }
        } else {
          // Default to exact match if no operator
          const threshold = parseInt(filterTrimmed);
          if (!isNaN(threshold)) {
            matches = matches && stockValue === threshold;
          }
        }
      }

      return matches;
    });

    setFilteredArticles(filtered);
  }, [allArticles]);

  // Initial load and refetch when codeEnt changes
  useEffect(() => {
    fetchInitialArticles();
  }, [fetchInitialArticles]);
  
  return {
    articles: filteredArticles,
    allArticles,
    total,
    totalLoaded: allArticles.length,
    initialLoading,
    backgroundLoading,
    error,
    applyFilters,
    refetch: fetchInitialArticles
  };
};

